import { GlobalOutlined } from '@ant-design/icons';
import { type MenuProps, Button, Dropdown, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AtlasGqlUserLanguage } from 'types/atlas-graphql';
import { useUpdateUserPreference } from 'utils/account/useUpdateUserPreference';
import {
  type SupportedLanguage,
  cacheLanguage,
  getSupportedLanguage,
  languageNames,
  supportedLanguages,
} from 'utils/i18n';
import { reloadPage } from 'utils/navigation';

type LanguageSwitcherProps = {
  /** Whether to show tooltip */
  showTooltip?: boolean;
  /** Additional CSS class */
  className?: string;
};

const getLanguageLabel = (language: SupportedLanguage) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <GlobalOutlined />
    {languageNames[language]}
  </span>
);

const userLanguages: Record<SupportedLanguage, AtlasGqlUserLanguage> = {
  de: AtlasGqlUserLanguage.De,
  en: AtlasGqlUserLanguage.En,
  es: AtlasGqlUserLanguage.Es,
  fr: AtlasGqlUserLanguage.Fr,
  it: AtlasGqlUserLanguage.It,
  pt: AtlasGqlUserLanguage.Pt,
};

export const LanguageSwitcher = ({ showTooltip = true, className }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const [isReloadPending, setIsReloadPending] = useState(false);
  // The reload refetches UserDetails. Avoid updating it from this response first,
  // which would make GlobalHeader reload before the success message closes.
  const { loading, update } = useUpdateUserPreference({
    successMessage: t('common.languageUpdateSuccess', 'Language preference updated successfully.'),
    errorMessage: t(
      'common.languageUpdateError',
      'Unable to update language preference. Please try again.'
    ),
    mutationOptions: { fetchPolicy: 'no-cache' },
  });
  const currentLanguage = getSupportedLanguage(i18n.language) ?? 'en';
  const isChanging = loading || isReloadPending;

  const handleLanguageChange = useCallback(
    async (language: SupportedLanguage) => {
      if (language === currentLanguage) {
        return;
      }

      await update(
        { language: userLanguages[language] },
        {
          successDuration: 1,
          resetGuardOnSuccess: false,
          onSuccess: () => {
            setIsReloadPending(true);
            // Cache before the delay so an unload inside it (a reload triggered
            // elsewhere, /reset, or a manual refresh) cannot leave the saved
            // preference and the bootstrap cache disagreeing.
            cacheLanguage(language);
            // Reload after the toast duration. Do not hang this on message onClose:
            // destroy/destroyAll would skip the reload and leave the switcher wedged.
            // The live i18n language is left untouched on purpose. The reload boots
            // from the cache above, and switching it here while the cached UserDetails
            // still holds the previous language makes GlobalHeader treat this as a
            // stale language, revert it, and reload a second time.
            window.setTimeout(() => {
              reloadPage();
            }, 1000);
          },
        }
      );
    },
    [currentLanguage, update]
  );

  const languageChoices: MenuProps['items'] = useMemo(() => {
    const currentLanguageItem = {
      key: currentLanguage,
      label: getLanguageLabel(currentLanguage),
      disabled: isChanging,
      onClick: () => handleLanguageChange(currentLanguage),
    };

    const additionalLanguages = supportedLanguages
      .filter((language) => language !== currentLanguage)
      .map((language) => ({
        key: language,
        label: getLanguageLabel(language),
        disabled: isChanging,
        onClick: () => handleLanguageChange(language),
      }));

    if (!additionalLanguages.length) {
      return [currentLanguageItem];
    }

    return [currentLanguageItem, { type: 'divider' as const }, ...additionalLanguages];
  }, [currentLanguage, handleLanguageChange, isChanging]);

  const languageName = (
    <>
      <GlobalOutlined />
      &nbsp;
      {languageNames[currentLanguage]}
    </>
  );

  const trigger = (
    <Button
      type="link"
      disabled={isChanging}
      loading={isChanging}
      className={className}
      data-testid="language-switcher"
      aria-label={t('common.selectLanguage', 'Select Language')}
    >
      {languageName}
    </Button>
  );

  const switcher = (
    <Dropdown menu={{ items: languageChoices }} trigger={['hover', 'click']}>
      {trigger}
    </Dropdown>
  );

  if (showTooltip) {
    return (
      <Tooltip title={t('common.selectLanguage', 'Select Language')} placement="bottom">
        {switcher}
      </Tooltip>
    );
  }

  return switcher;
};

export default LanguageSwitcher;
